<div class="analytics-page">

  <app-page-header
    title="Support Analytics"
    subtitle="Overview of support ticket performance and trends"
    [showBackButton]="true"
    backRoute="/help/tickets"
    backLabel="Back to Inbox">
    <mat-form-field appearance="outline"
      class="support-select"
      subscriptSizing="dynamic">
      <mat-label>Period</mat-label>
      <mat-select
        [(ngModel)]="selectedPeriod"
        name="selectedPeriod"
        (selectionChange)="onPeriodChange()"
        panelClass="owner-station-filter-panel">
        <mat-option *ngFor="let p of periods" [value]="p.value">
          {{ p.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>
  </app-page-header>

  <div class="analytics-loader" *ngIf="loading">
    <app-data-loader [show]="true"
      message="Loading analytics..."
      size="large" />
  </div>

  <div class="analytics-error" *ngIf="!loading && !analytics">
    <mat-icon>error_outline</mat-icon>
    <p>We couldn't load analytics right now.</p>
    <button class="btn-secondary btn-sm"
      type="button"
      (click)="loadAnalytics()">
      Try again
    </button>
  </div>

  <ng-container *ngIf="!loading && analytics">

    <!-- KPI cards -->
    <div class="analytics-stat-grid">

      <div class="analytics-stat analytics-stat--total">
        <div class="analytics-stat__icon">
          <mat-icon>confirmation_number</mat-icon>
        </div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">
            {{ analytics.overview.total_tickets }}
          </span>
          <span class="analytics-stat__label">Total Tickets</span>
          <span class="analytics-stat__meta">{{ periodLabel }}</span>
        </div>
      </div>

      <div class="analytics-stat analytics-stat--open">
        <div class="analytics-stat__icon">
          <mat-icon>inbox</mat-icon>
        </div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">
            {{ analytics.overview.open_count }}
          </span>
          <span class="analytics-stat__label">Open Tickets</span>
          <span class="analytics-stat__meta">
            {{ analytics.overview.in_progress_count }} in progress
          </span>
        </div>
      </div>

      <div class="analytics-stat analytics-stat--resolved">
        <div class="analytics-stat__icon">
          <mat-icon>task_alt</mat-icon>
        </div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">
            {{ analytics.overview.resolved_count + analytics.overview.closed_count }}
          </span>
          <span class="analytics-stat__label">Resolved / Closed</span>
          <span class="analytics-stat__meta">
            {{ analytics.overview.closed_count }} closed
          </span>
        </div>
      </div>

      <div class="analytics-stat analytics-stat--rate">
        <div class="analytics-stat__icon">
          <mat-icon>trending_up</mat-icon>
        </div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value"
            [style.color]="getResolutionColor()">
            {{ analytics.overview.resolution_rate | number:'1.0-1' }}%
          </span>
          <span class="analytics-stat__label">Resolution Rate</span>
          <div class="analytics-stat__bar">
            <div class="analytics-stat__bar-fill"
              [style.width.%]="analytics.overview.resolution_rate"
              [style.background]="getResolutionColor()">
            </div>
          </div>
        </div>
      </div>

      <div class="analytics-stat analytics-stat--time">
        <div class="analytics-stat__icon">
          <mat-icon>schedule</mat-icon>
        </div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">
            {{ analytics.overview.avg_resolution_hours | number:'1.0-1' }}<small>h</small>
          </span>
          <span class="analytics-stat__label">Avg. Resolution Time</span>
          <span class="analytics-stat__meta">Across resolved tickets</span>
        </div>
      </div>

      <div class="analytics-stat analytics-stat--priority">
        <div class="analytics-stat__icon">
          <mat-icon>priority_high</mat-icon>
        </div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">
            {{ analytics.overview.high_priority_count }}
          </span>
          <span class="analytics-stat__label">High Priority</span>
          <span class="analytics-stat__meta">Needs fast response</span>
        </div>
      </div>
    </div>

    <div class="analytics-grid-2">

      <!-- Tickets by category -->
      <section class="analytics-card">
        <header class="analytics-card__head">
          <h3 class="analytics-card__title">Tickets by Category</h3>
          <span class="analytics-card__hint">
            Share of total tickets
          </span>
        </header>

        <div class="category-bars"
          *ngIf="analytics.by_category.length > 0">
          <div class="category-bar"
            *ngFor="let cat of analytics.by_category">
            <div class="category-bar__top">
              <span class="category-bar__label">
                {{ getCategoryLabel(cat.category) }}
              </span>
              <span class="category-bar__value">
                {{ cat.count }}
                <small>
                  ({{ categoryWidth(cat.count) | number:'1.0-0' }}%)
                </small>
              </span>
            </div>
            <div class="category-bar__track">
              <div class="category-bar__fill"
                [style.width.%]="categoryWidth(cat.count)">
              </div>
            </div>
          </div>
        </div>

        <div class="card-empty"
          *ngIf="analytics.by_category.length === 0">
          <mat-icon>donut_large</mat-icon>
          <p>No tickets in this period.</p>
        </div>
      </section>

      <!-- Routing -->
      <section class="analytics-card">
        <header class="analytics-card__head">
          <h3 class="analytics-card__title">Ticket Routing</h3>
          <span class="analytics-card__hint">
            Where tickets land
          </span>
        </header>

        <div class="routing-list"
          *ngIf="analytics.by_routing.length > 0">
          <div class="routing-item"
            *ngFor="let r of analytics.by_routing">
            <span class="routing-item__dot"
              [class.routing-item__dot--admin]="r.routed_to === 'admin'"
              [class.routing-item__dot--station]="r.routed_to === 'station'">
            </span>
            <div class="routing-item__body">
              <div class="routing-item__top">
                <span class="routing-item__label">
                  {{ r.routed_to === 'admin' ? 'RAD Admin' : 'Station Staff' }}
                </span>
                <span class="routing-item__count">
                  {{ r.count }}
                  <small>
                    ({{ routingPct(r.count) | number:'1.0-0' }}%)
                  </small>
                </span>
              </div>
              <div class="routing-item__track">
                <div class="routing-item__fill"
                  [class.routing-item__fill--admin]="r.routed_to === 'admin'"
                  [class.routing-item__fill--station]="r.routed_to === 'station'"
                  [style.width.%]="routingPct(r.count)">
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-empty"
          *ngIf="analytics.by_routing.length === 0">
          <mat-icon>alt_route</mat-icon>
          <p>No routing data yet.</p>
        </div>
      </section>
    </div>

    <!-- Volume over time -->
    <section class="analytics-card">
      <header class="analytics-card__head">
        <h3 class="analytics-card__title">Ticket Volume Over Time</h3>
        <div class="chart-legend">
          <span class="chart-legend__item chart-legend__item--created">
            Created
          </span>
          <span class="chart-legend__item chart-legend__item--resolved">
            Resolved
          </span>
        </div>
      </header>

      <div class="time-chart" *ngIf="analytics.over_time.length > 0">
        <div class="time-chart__axis">
          <span>{{ chartMax }}</span>
          <span>{{ chartMax / 2 | number:'1.0-0' }}</span>
          <span>0</span>
        </div>

        <div class="time-chart__plot">
          <span class="time-chart__gridline"></span>
          <span class="time-chart__gridline"></span>
          <span class="time-chart__gridline"></span>

          <div class="time-chart__cols">
            <div class="time-col"
              *ngFor="let day of analytics.over_time">
              <div class="time-col__bars">
                <div class="time-col__bar time-col__bar--created"
                  [style.height.%]="barPct(day.created)"
                  [title]="'Created: ' + day.created">
                </div>
                <div class="time-col__bar time-col__bar--resolved"
                  [style.height.%]="barPct(day.resolved)"
                  [title]="'Resolved: ' + day.resolved">
                </div>
              </div>
              <span class="time-col__label">
                {{ day.date | date:'MMM d' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="card-empty"
        *ngIf="analytics.over_time.length === 0">
        <mat-icon>bar_chart</mat-icon>
        <p>No ticket activity in this period.</p>
      </div>
    </section>

    <!-- Top stations -->
    <section class="analytics-card"
      *ngIf="analytics.top_stations.length > 0">
      <header class="analytics-card__head">
        <h3 class="analytics-card__title">
          Stations with Most Tickets
        </h3>
      </header>

      <div class="analytics-table-wrap">
        <table class="analytics-table">
          <thead>
            <tr>
              <th>Station</th>
              <th>Total</th>
              <th>Open</th>
              <th>Resolution</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let s of analytics.top_stations">
              <td>
                <span class="station-name">
                  {{ s.station_name }}
                </span>
                <small class="station-call-sign">
                  {{ s.call_sign }}
                </small>
              </td>
              <td>{{ s.ticket_count }}</td>
              <td>
                <span [class]="s.open_count > 0 ? 'text-orange' : 'text-green'">
                  {{ s.open_count }}
                </span>
              </td>
              <td>
                <div class="table-progress">
                  <div class="table-progress__track">
                    <div class="table-progress__fill"
                      [style.width.%]="stationResolution(s.ticket_count, s.open_count)">
                    </div>
                  </div>
                  <span class="table-progress__value">
                    {{ stationResolution(s.ticket_count, s.open_count) | number:'1.0-0' }}%
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

  </ng-container>
</div>
